/**
 * The detail of one inbox row (one rule in one source): the rule that raised it, worded as the
 * Rules tab words it, with a link to that rule; the source it came from, with a link to the
 * document; and every finding in full, each with its triage. It sits beside the inbox, or in a
 * panel on a narrow screen (see `MonitorWorkspace`).
 *
 * Presentational: the workspace finds the rule and passes the ways to open it and the document.
 */
import { BellRing, ExternalLink, FileText, ListChecks, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { formatDate } from '@/utils/date'
import { type AlertGroupRow, type AlertInboxRow, firstEventAt } from './AlertInbox'
import {
  AlertFindingLine,
  AlertObjectLabel,
  type AlertObjectRow,
  alertObjectText,
} from './AlertObject'
import { AlertTriageLine, AlertTriageMenu, type AlertTriageProps } from './AlertTriage'
import { type RuleRow, Watches } from './RulesList'
import { useRuleLabel } from './rule-name'
import { SeverityBadge } from './SeverityBadge'

const DETAIL = 'cases.monitor.detail'
const INBOX = 'cases.monitor.inbox'

/** Where the detail leads, and how it closes. */
export interface AlertDetailActions {
  onClose: () => void
  onOpenDocument: (documentId: string, atSeconds?: number) => void
  /** Shows the rule in the Rules tab. */
  onOpenRule: (ruleId: string) => void
}

export interface AlertDetailProps {
  group: AlertGroupRow
  /** The rule as the Rules tab lists it; null when it was deleted (its alerts stay); left out while the rules are read. */
  rule?: RuleRow | null
  triage: AlertTriageProps | undefined
  actions: AlertDetailActions
}

function Block({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <span className="text-muted-foreground">{icon}</span>
        {title}
      </h3>
      {children}
    </section>
  )
}

function RuleBlock({
  group,
  rule,
  onOpenRule,
}: {
  group: AlertGroupRow
  rule: RuleRow | null | undefined
  onOpenRule: (ruleId: string) => void
}) {
  const { t } = useTranslation()
  const ruleLabel = useRuleLabel()
  const ruleId = group.alerts[0].ruleId
  const kind = rule?.kind ?? group.ruleKind
  const severity = rule?.severity ?? group.severity
  return (
    <Block icon={<ListChecks aria-hidden className="size-4" />} title={t(`${DETAIL}.rule`)}>
      <div className="flex flex-col gap-2 rounded-md border border-border/60 bg-muted/30 p-3">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">
              {ruleLabel(rule ? rule.name : group.ruleName)}
            </p>
            <p className="text-xs text-muted-foreground">
              {t(`cases.monitor.rules.kinds.${kind ?? 'unknown'}`)}
            </p>
          </div>
          {severity ? <SeverityBadge severity={severity} /> : null}
        </div>
        {kind && kind !== 'occurrence' ? (
          <p className="text-xs text-muted-foreground">
            {t(`cases.monitor.rules.form.hints.${kind}`)}
          </p>
        ) : null}
        {rule ? <Watches row={rule} /> : null}
        {rule === null ? (
          <p className="text-xs text-muted-foreground">{t(`${DETAIL}.ruleGone`)}</p>
        ) : null}
      </div>
      {rule ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          onClick={() => onOpenRule(ruleId)}
        >
          <ExternalLink aria-hidden />
          {t(`${DETAIL}.openRule`)}
        </Button>
      ) : null}
    </Block>
  )
}

function SourceBlock({
  group,
  onOpenDocument,
}: {
  group: AlertGroupRow
  onOpenDocument: (documentId: string, atSeconds?: number) => void
}) {
  const { t, i18n } = useTranslation()
  const learned = formatDate(group.learnedAt, i18n.language)
  const name = group.documentName ?? t(`${INBOX}.unknownDocument`)
  return (
    <Block icon={<FileText aria-hidden className="size-4" />} title={t(`${DETAIL}.source`)}>
      <div className="flex flex-col gap-2 rounded-md border border-border/60 bg-muted/30 p-3">
        {group.documentId || group.documentName ? (
          <p className="break-all text-sm font-medium text-foreground">{name}</p>
        ) : (
          <p className="text-xs text-muted-foreground">{t(`${DETAIL}.noDocument`)}</p>
        )}
        <p className="text-xs text-muted-foreground">
          <time dateTime={group.learnedAt}>{t(`${INBOX}.learnedOn`, { date: learned })}</time>
          {' · '}
          {t(`${INBOX}.hits`, { count: group.hits })}
        </p>
      </div>
      {group.documentId ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          onClick={() => onOpenDocument(group.documentId as string, firstEventAt(group))}
        >
          <ExternalLink aria-hidden />
          {t(`${DETAIL}.openDocument`)}
        </Button>
      ) : null}
    </Block>
  )
}

function Finding({
  alert,
  triage,
}: {
  alert: AlertInboxRow
  triage: AlertTriageProps | undefined
}) {
  const { t } = useTranslation()
  const object: AlertObjectRow = alert
  return (
    <li className="flex items-start gap-2 py-2.5 first:pt-0 last:pb-0">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm text-foreground">
          <span className="break-words font-medium">
            <AlertObjectLabel row={object} />
          </span>
          {alert.subjectType ? <Badge variant="outline">{alert.subjectType}</Badge> : null}
          {alert.isNew ? <Badge variant="info">{t(`${INBOX}.new`)}</Badge> : null}
        </div>
        <AlertFindingLine finding={alert.finding} full />
        {alert.triage && triage ? (
          <AlertTriageLine row={alert.triage} onOpenInvestigation={triage.onOpenInvestigation} />
        ) : null}
      </div>
      {triage?.actions ? (
        <AlertTriageMenu
          alerts={[{ id: alert.id, state: alert.triage?.state ?? null }]}
          name={alertObjectText(alert, t)}
          actions={triage.actions}
        />
      ) : null}
    </li>
  )
}

export function AlertDetail({ group, rule, triage, actions }: AlertDetailProps) {
  const { t } = useTranslation()
  return (
    <article
      className="flex min-w-0 flex-col gap-5 p-4 [overflow-wrap:anywhere]"
      aria-label={t(`${DETAIL}.label`)}
    >
      <header className="sticky top-0 z-10 -mx-4 -mt-4 flex items-center gap-2 border-b bg-card px-4 py-3">
        <h2 className="min-w-0 flex-1 text-base font-semibold text-foreground">
          {t(`${DETAIL}.title`)}
        </h2>
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label={t(`${DETAIL}.close`)}
          onClick={actions.onClose}
        >
          <X aria-hidden />
        </Button>
      </header>
      <RuleBlock group={group} rule={rule} onOpenRule={actions.onOpenRule} />
      <SourceBlock group={group} onOpenDocument={actions.onOpenDocument} />
      <Block
        icon={<BellRing aria-hidden className="size-4" />}
        title={t(`${INBOX}.findings`, { count: group.alerts.length })}
      >
        <ul className="flex flex-col divide-y divide-border/60">
          {group.alerts.map(alert => (
            <Finding key={alert.id} alert={alert} triage={triage} />
          ))}
        </ul>
      </Block>
    </article>
  )
}
